/**
 * BroadcastDetailsModal - Modal de detalhes do broadcast
 * Versão mobile-first
 */
import React from 'react'
import ReactMarkdown from 'react-markdown'
import { Bell, Mail, Calendar, User, RefreshCw } from 'lucide-react'
import { Modal } from '../../components/molecules/Modal'
import Card from '../../components/atoms/Card'
import Button from '../../components/atoms/Button'
import Badge from '../../components/atoms/Badge'
import { formatDate, formatNumber } from '../../core/admin.utils'
import {
	BROADCAST_TYPES,
	BROADCAST_STATUS,
	AUDIENCE_OPTIONS,
	MARKDOWN_PREVIEW_STYLES,
} from '../core/broadcast.constants'

const BroadcastDetailsModal = ({
	isOpen,
	onClose,
	broadcast,
	onResend,
}) => {
	if (!broadcast) return null

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Detalhes do Broadcast"
			size="lg"
		>
			<div className="space-y-4 md:space-y-6">
				<style>{MARKDOWN_PREVIEW_STYLES}</style>

				{/* Info Geral */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
					<Card className="p-3 md:p-4">
						<div className="flex items-center gap-2 mb-1 md:mb-2">
							<Calendar className="w-4 h-4 text-text-muted" />
							<p className="text-xs md:text-sm text-text-muted">Enviado em</p>
						</div>
						<p className="text-text font-medium text-sm md:text-base">
							{formatDate(broadcast.sentAt || broadcast.createdAt, 'dd/MM/yyyy HH:mm')}
						</p>
					</Card>
					<Card className="p-3 md:p-4">
						<div className="flex items-center gap-2 mb-1 md:mb-2">
							<User className="w-4 h-4 text-text-muted" />
							<p className="text-xs md:text-sm text-text-muted">Enviado por</p>
						</div>
						<p className="text-text font-medium text-sm md:text-base">
							{broadcast.sentBy?.name || 'Admin'}
						</p>
					</Card>
				</div>

				{/* Tipo e Status */}
				<div className="flex flex-wrap items-center gap-2">
					<Badge variant={BROADCAST_TYPES[broadcast.type]?.color || 'blue'} className="text-xs">
						{BROADCAST_TYPES[broadcast.type]?.label || 'Push'}
					</Badge>
					<Badge variant={BROADCAST_STATUS[broadcast.status]?.color || 'gray'} className="text-xs">
						{BROADCAST_STATUS[broadcast.status]?.label || 'Enviado'}
					</Badge>
					<Badge variant="purple" className="text-xs">
						{AUDIENCE_OPTIONS.find(a => a.value === broadcast.targetAudience)?.label || 'Todos'}
					</Badge>
				</div>

				{/* Estatísticas */}
				<Card className="p-3 md:p-4">
					<h4 className="font-semibold text-text text-sm md:text-base mb-2 md:mb-3">Estatísticas</h4>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
						<div className="text-center">
							<p className="text-xl md:text-2xl font-bold text-text">
								{formatNumber(broadcast.stats?.totalTargeted || 0)}
							</p>
							<p className="text-xs text-text-muted">Total Alvos</p>
						</div>
						{(broadcast.type === 'push' || broadcast.type === 'both') && (
							<>
								<div className="text-center">
									<p className="text-xl md:text-2xl font-bold text-green-500">
										{formatNumber(broadcast.stats?.pushSent || 0)}
									</p>
									<p className="text-xs text-text-muted">Push Enviados</p>
								</div>
								<div className="text-center">
									<p className="text-xl md:text-2xl font-bold text-red-500">
										{formatNumber(broadcast.stats?.pushFailed || 0)}
									</p>
									<p className="text-xs text-text-muted">Push Falhas</p>
								</div>
							</>
						)}
						{(broadcast.type === 'email' || broadcast.type === 'both') && (
							<>
								<div className="text-center">
									<p className="text-xl md:text-2xl font-bold text-blue-500">
										{formatNumber(broadcast.stats?.emailSent || 0)}
									</p>
									<p className="text-xs text-text-muted">Emails Enviados</p>
								</div>
								<div className="text-center">
									<p className="text-xl md:text-2xl font-bold text-red-500">
										{formatNumber(broadcast.stats?.emailFailed || 0)}
									</p>
									<p className="text-xs text-text-muted">Emails Falhas</p>
								</div>
							</>
						)}
					</div>
				</Card>

				{/* Conteúdo Push */}
				{(broadcast.type === 'push' || broadcast.type === 'both') && (
					<Card className="p-3 md:p-4">
						<div className="flex items-center gap-2 mb-2 md:mb-3">
							<Bell className="w-4 h-4 md:w-5 md:h-5 text-blue-500" />
							<h4 className="font-semibold text-text text-sm md:text-base">Notificação Push</h4>
						</div>
						<div className="bg-base-400 rounded-lg p-3 md:p-4 space-y-2">
							<div>
								<p className="text-xs text-text-muted">Título</p>
								<p className="font-medium text-text text-sm md:text-base">
									{broadcast.push?.title || broadcast.title || '-'}
								</p>
							</div>
							<div>
								<p className="text-xs text-text-muted">Mensagem</p>
								<div className="markdown-preview text-text-body text-sm">
									<ReactMarkdown>
										{broadcast.push?.body || broadcast.message || '-'}
									</ReactMarkdown>
								</div>
							</div>
						</div>
					</Card>
				)}

				{/* Conteúdo Email */}
				{(broadcast.type === 'email' || broadcast.type === 'both') && (
					<Card className="p-3 md:p-4">
						<div className="flex items-center gap-2 mb-2 md:mb-3">
							<Mail className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
							<h4 className="font-semibold text-text text-sm md:text-base">E-mail</h4>
						</div>
						<div className="bg-base-400 rounded-lg p-3 md:p-4 space-y-2">
							<div>
								<p className="text-xs text-text-muted">Assunto</p>
								<p className="font-medium text-text text-sm md:text-base">
									{broadcast.email?.subject || broadcast.title || '-'}
								</p>
							</div>
							{broadcast.email?.title && (
								<div>
									<p className="text-xs text-text-muted">Título</p>
									<p className="font-medium text-text text-sm">
										{broadcast.email.title}
									</p>
								</div>
							)}
							<div>
								<p className="text-xs text-text-muted">Corpo</p>
								<div className="markdown-preview text-text-body text-sm">
									<ReactMarkdown>
										{broadcast.email?.body || broadcast.message || '-'}
									</ReactMarkdown>
								</div>
							</div>
						</div>
					</Card>
				)}

				{/* Actions - Mobile friendly */}
				<div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-2 border-t border-base-200 sm:border-0 sm:pt-0">
					<Button variant="outline" onClick={onResend} className="w-full sm:w-auto">
						<RefreshCw className="w-4 h-4 mr-2" />
						Reenviar
					</Button>
					<Button onClick={onClose} className="w-full sm:w-auto">
						Fechar
					</Button>
				</div>
			</div>
		</Modal>
	)
}

export default BroadcastDetailsModal
